// Build the destination URL for the "Book your check-up" / "Become a member"
// CTA on the insurance coverage result screen.
//
// On the membership-first site (CH today) every checker page books into the
// new app, book.aheadhealth.com, like the rest of the site: the package and
// add-ons ride in `products=` and the app picks its own entry step (see
// `membershipFirstBookingUrl` in lib/booking.ts). A membership result opens
// the app on its plan, with nothing in `products`.
//
// Elsewhere it routes to the current booking app (one onboarding flow — the
// standalone membership.aheadhealth.com app is deprecated for acquisition):
//   - Memberships → https://booking.aheadhealth.com/?packageType=blood-only&step=cadence
//   - Everything else → https://booking.aheadhealth.com/?products=…&step=addons
//
// In both cases we preserve any existing utm_* params on the parent page —
// a user who landed from a paid IG ad keeps Instagram attribution all the
// way through to booking. Only if there are no incoming UTMs do we tag with
// our own (utm_source=insurance_checker, utm_medium=embed, …) so PostHog can
// still attribute the conversion.

import {
  BOOKING_PACKAGE_TYPE,
  PRODUCT_IDS,
  membershipFirstBookingUrl,
} from "@/lib/booking"
import type { BookingFlow } from "@/lib/membership-first"
import { applyPersonHash } from "@/lib/person-hash-link"

const BOOKING_URL = "https://booking.aheadhealth.com"

/**
 * Insurance-checker service IDs (from lib/insurance/insurance-services.ts) →
 * booking-app product UUIDs that the `?products=` parser recognises (see
 * ahead-booking-new stores/bookingFlowStore.ts → initializeFromUrl). UUIDs
 * are reused from lib/booking.ts PRODUCT_IDS so there's one source of truth.
 *
 * Add-ons are NOT here — the checker's selectedAddons Map keys by the Supabase
 * product UUID (lib/insurance/addons.ts → Addon.id = p.id), so we pass them
 * straight through.
 *
 * "blood-test" maps to the blood-only package, not the standalone "Advanced
 * blood test" add-on (which has a separate UUID and rides in the addons set
 * when picked as an extra on top of an MRI).
 */
const SERVICE_TO_PRODUCT_UUID: Record<string, string> = {
  "core-mri": PRODUCT_IDS.core,
  "advanced-mri": PRODUCT_IDS.advanced,
  "pro-mri": PRODUCT_IDS.pro,
  "blood-test": PRODUCT_IDS.bloodTest,
}

/**
 * Insurance-checker membership service IDs → plan keys (historically the
 * membership app's RecommendedPlan values). Forwarded on the booking-app
 * cadence link for future plan preselection — the booking app ignores the
 * param today.
 */
const MEMBERSHIP_PLAN_KEY: Record<string, string> = {
  "membership-basic": "baseline",
  "membership-plus-1": "twice",
  "membership-plus-3": "quarterly",
}

/** UTM-family params we recognise and preserve when forwarding. */
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
] as const

export function isMembershipService(serviceId: string): boolean {
  return serviceId.startsWith("membership-")
}

export interface BuildBookingUrlInput {
  /** Insurance-checker service ID (e.g. "core-mri", "membership-plus-1"). */
  serviceId: string
  /** Supabase product UUIDs for any selected add-ons. */
  addonProductIds?: string[]
  /**
   * Search string from the *parent page* (e.g. `window.location.search`). Any
   * `utm_*` params here are forwarded unchanged so paid-acquisition
   * attribution survives the cross-domain hop. Defaults to "" so the helper is
   * usable in tests and SSR without a window.
   */
  incomingSearch?: string
  /**
   * Which booking app the page books into (`useBookingFlow()`). Defaults to
   * the current app, `legacy`.
   */
  flow?: BookingFlow
  /** Market and language of the page, for the membership-first app. */
  country?: string
  locale?: string
}

export function buildBookingUrl({
  serviceId,
  addonProductIds = [],
  incomingSearch = "",
  flow = "legacy",
  country = "ch",
  locale = "de",
}: BuildBookingUrlInput): string {
  if (flow === "membership-first") {
    return membershipFirstCheckerUrl(
      serviceId,
      addonProductIds,
      incomingSearch,
      country,
      locale
    )
  }

  const params = new URLSearchParams()
  applyUtms(params, incomingSearch, serviceId)
  // Carry the email→app person hash across the hop so a session that arrived
  // from an email link stays the same PostHog person in booking/membership.
  applyPersonHash(params, incomingSearch)

  if (isMembershipService(serviceId)) {
    // Aim at the booking app's cadence step — the plan chooser. The app
    // doesn't read a plan-preselect param yet, so the user re-picks their
    // cadence there; `plan` is still forwarded so the app can preselect once
    // it learns to read it (harmless until then).
    const plan = MEMBERSHIP_PLAN_KEY[serviceId]
    if (plan) params.set("plan", plan)
    // `blood-only`, not `membership` — same reasoning as
    // `membershipBookingAppUrl` in lib/booking.ts (AHD-9123): the membership
    // tab dead-ends on a card that links out to the deprecated membership app,
    // the blood tab shows the membership/single-test comparison and its CTA
    // enters the in-app cadence step.
    params.set("packageType", "blood-only")
    params.set("step", "cadence")
    return `${BOOKING_URL}/?${params.toString()}`
  }

  const productUuid = SERVICE_TO_PRODUCT_UUID[serviceId]
  const productIds = productUuid
    ? [productUuid, ...addonProductIds]
    : [...addonProductIds]
  if (productIds.length > 0) {
    params.set("products", productIds.join(","))
    // Skip the booking app's product picker — the user already chose in the
    // checker. Land them on the add-ons step (the next real decision).
    params.set("step", "addons")
  }
  return `${BOOKING_URL}/?${params.toString()}`
}

function membershipFirstCheckerUrl(
  serviceId: string,
  addonProductIds: string[],
  incomingSearch: string,
  country: string,
  locale: string
): string {
  const productUuid = SERVICE_TO_PRODUCT_UUID[serviceId]
  const productIds = isMembershipService(serviceId)
    ? []
    : productUuid
      ? [productUuid, ...addonProductIds]
      : [...addonProductIds]
  const packageType = serviceId.endsWith("-mri")
    ? BOOKING_PACKAGE_TYPE.mri
    : BOOKING_PACKAGE_TYPE.blood
  const url = new URL(
    membershipFirstBookingUrl(country, locale, productIds, packageType)
  )
  applyUtms(url.searchParams, incomingSearch, serviceId)
  applyPersonHash(url.searchParams, incomingSearch)
  return url.toString()
}

function applyUtms(
  out: URLSearchParams,
  incomingSearch: string,
  serviceId: string
): void {
  const incoming = new URLSearchParams(incomingSearch)
  // If the user arrived with any utm_source, treat the whole UTM family as
  // their attribution chain and forward it unchanged. This preserves the
  // first-touch source (e.g. paid IG → /insurance → /booking) rather than
  // overwriting it with the embed's last-touch source.
  if (incoming.has("utm_source")) {
    for (const key of UTM_KEYS) {
      const val = incoming.get(key)
      if (val !== null) out.set(key, val)
    }
    return
  }
  // No incoming UTMs — tag the click as coming from the insurance checker so
  // PostHog can still attribute downstream conversions.
  out.set("utm_source", "insurance_checker")
  out.set("utm_medium", "embed")
  out.set("utm_campaign", "coverage_result")
  out.set("utm_content", serviceId)
}
